/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";

interface EditRouterInfoValues {
  routerName: string;
  macAddress: string;
  publicIp: string;
}

interface Props {
  initialValues: EditRouterInfoValues;
  saving: boolean;
  onSubmit: (values: EditRouterInfoValues) => void;
  onCancel: () => void;
}

const EditRouterInfo: React.FC<Props> = ({
  initialValues,
  saving,
  onSubmit,
  onCancel,
}) => {
  const [form, setForm] = useState<EditRouterInfoValues>(initialValues);
  const [errors, setErrors] = useState<{ routerName?: string; macAddress?: string; publicIp?: string }>({});
  
  const macRegex = /^([0-9A-Fa-f]{2}([-:]?)){5}[0-9A-Fa-f]{2}$/;
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

  useEffect(() => {
    setForm(initialValues);
    setErrors({});
  }, [initialValues]);

  const isChanged = useMemo(() => {
    return (
      form.routerName.trim() !== initialValues.routerName.trim() ||
      form.macAddress.trim() !== initialValues.macAddress.trim() ||
      form.publicIp.trim() !== initialValues.publicIp.trim()
    );
  }, [form, initialValues]);

  const getRouterNameError = (routerName: string) => {
    if (!routerName) return "Router name is required";
    if (routerName !== routerName.trim()) return "Enter a valid router name";
    return "";
  };

  const getMacAddressError = (macAddress: string) => {
    if (!macAddress) return "MAC address is required";
    if (macAddress !== macAddress.trim()) return "Enter a valid MAC address";
    if (!macRegex.test(macAddress.trim())) return "Enter a valid MAC address";
    return "";
  };

  const getPublicIpError = (publicIp: string) => {
    if (!publicIp) return "Public IP is required";
    if (!ipRegex.test(publicIp.trim())) return "Enter a valid IP address";
    return "";
  };

  const validate = () => {
    const nextErrors = {
      routerName: getRouterNameError(form.routerName),
      macAddress: getMacAddressError(form.macAddress),
      publicIp: getPublicIpError(form.publicIp),
    };
    setErrors(nextErrors);
    return !nextErrors.routerName && !nextErrors.macAddress && !nextErrors.publicIp;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    onSubmit({
      routerName: form.routerName.trim(),
      macAddress: form.macAddress.trim(),
      publicIp: form.publicIp.trim(),
    });
  };

  const handleChange = (
    key: keyof EditRouterInfoValues,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;

    switch (key) {
      case "routerName":
        value = value.replace(/[^a-zA-Z0-9\s,._]/g, "");
        break;

      case "macAddress":
        value = value.replace(/[^a-fA-F0-9:]/g, "");
        break;

      case "publicIp":
        value = value.replace(/[^0-9.]/g, "");
        break;
    }

    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => {
      const next = { ...prev };

      if (key === "routerName") {
        next.routerName = "";
      }

      if (key === "macAddress") {
        if (!value) {
          next.macAddress = "MAC address is required";
        } else if (!macRegex.test(value.trim())) {
          next.macAddress = "Enter a valid MAC address";
        } else {
          next.macAddress = "";
        }
      }

      if (key === "publicIp") {
        if (!value) {
          next.publicIp = "Public IP is required";
        } else if (!ipRegex.test(value.trim())) {
          next.publicIp = "Enter a valid IP address";
        } else {
          next.publicIp = "";
        }
      }

      return next;
    });
  };

  return (
    <div className="p-5 space-y-4">
      <div>
        <label htmlFor="edit-router-name" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          Router Name <span className="text-red-500">*</span>
        </label>
        <input
          id="edit-router-name"
          placeholder="Router Name"
          value={form.routerName}
          onChange={(e) => handleChange("routerName", e)}
          className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-1 focus:ring-primary"
        />
        {errors.routerName && (
          <p className="text-red-500 text-xs mt-1">{errors.routerName}</p>
        )}
      </div>

      <div>
        <label htmlFor="edit-router-mac" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          MAC Address <span className="text-red-500">*</span>
        </label>
        <input
          id="edit-router-mac"
          placeholder="MAC Address"
          value={form.macAddress}
          onChange={(e) => handleChange("macAddress", e)}
          className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-1 focus:ring-primary"
        />
        {errors.macAddress && (
          <p className="text-red-500 text-xs mt-1">{errors.macAddress}</p>
        )}
      </div>

      <div>
        <label htmlFor="edit-router-ip" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          Public IP <span className="text-red-500">*</span>
        </label>
        <input
          id="edit-router-ip"
          placeholder="e.g. 192.168.1.1"
          value={form.publicIp}
          onChange={(e) => handleChange("publicIp", e)}
          className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-1 focus:ring-primary"
        />
        {errors.publicIp && (
          <p className="text-red-500 text-xs mt-1">{errors.publicIp}</p>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 mt-5">
        <button
          type="button"
          onClick={onCancel}
        className="px-3 py-2.5 text-sm font-medium text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-xl border border-gray-200 hover:text-gray-900 transition-all active:scale-95"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isChanged || saving}
        className={`text-white bg-blue-700 font-medium rounded-xl text-sm px-5 py-2.5 transition-all shadow-md
      ${(!isChanged || saving)
            ? "opacity-50 cursor-not-allowed bg-blue-700/50"
            : "hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 active:scale-95"
          }`}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default EditRouterInfo;